// Move-line-up/down for textareas — Walter 2026-05-18.
// Mirrors Google Docs / VS Code Alt+Shift+↑/↓ behaviour:
//   • If there's a selection spanning multiple lines, the WHOLE block
//     of selected lines moves as a unit (and the selection stays on
//     the moved block after the operation, so repeated presses keep
//     moving the same lines).
//   • If there's no multi-line selection, the line containing the
//     cursor moves as a single-line block.
//   • Up-arrow at the top is a no-op; down-arrow at the bottom is a
//     no-op. We don't wrap.
//
// Returns either { nextValue, nextSelStart, nextSelEnd } if the move
// was possible, or null for a no-op (caller should NOT preventDefault
// in that case).

// Given a value and a [selStart, selEnd] range, compute the [first
// line index, last line index] (inclusive) that the selection touches.
function lineRangeForSelection(value, selStart, selEnd) {
  const lines = value.split('\n');
  // Walk char offsets to find which lines selStart/selEnd fall in.
  let pos = 0;
  let firstLine = 0;
  let lastLine = 0;
  let foundFirst = false;
  for (let i = 0; i < lines.length; i += 1) {
    const lineLen = lines[i].length;
    const lineEnd = pos + lineLen; // exclusive of trailing \n
    if (!foundFirst && selStart <= lineEnd) {
      firstLine = i;
      foundFirst = true;
    }
    if (selEnd <= lineEnd) {
      lastLine = i;
      break;
    }
    lastLine = i;
    pos = lineEnd + 1; // skip the newline
  }
  // If selEnd happens to land EXACTLY at the start of a line (i.e.
  // right after a newline) and the selection is non-empty, the user
  // really means the line before — otherwise hitting Alt+Shift+↓ with
  // a fresh line selected would treat the empty next line as part of
  // the block. Only trim if the selection spans MULTIPLE lines.
  if (selEnd > selStart && lastLine > firstLine) {
    const lastLineStart = startOffsetOfLine(value, lastLine);
    if (selEnd === lastLineStart) lastLine -= 1;
  }
  return { lines, firstLine, lastLine };
}

function startOffsetOfLine(value, lineIdx) {
  if (lineIdx === 0) return 0;
  let pos = 0;
  let count = 0;
  for (let i = 0; i < value.length; i += 1) {
    if (value.charCodeAt(i) === 10 /* \n */) {
      count += 1;
      if (count === lineIdx) return i + 1;
    }
  }
  return pos;
}

// Move the [firstLine..lastLine] block up by one (swap with the line
// above). Returns updated state or null if at top.
export function moveLinesUp(value, selStart, selEnd) {
  const { lines, firstLine, lastLine } = lineRangeForSelection(value, selStart, selEnd);
  if (firstLine === 0) return null; // already at top
  // Swap the line above with the block: lines[firstLine-1] becomes a
  // new line right after the block.
  const above = lines[firstLine - 1];
  const block = lines.slice(firstLine, lastLine + 1);
  const newLines = [
    ...lines.slice(0, firstLine - 1),
    ...block,
    above,
    ...lines.slice(lastLine + 1),
  ];
  const nextValue = newLines.join('\n');
  // Selection follows the block UP by one line. Recompute char offsets.
  const aboveLen = above.length + 1; // +1 for the newline that joined them
  const nextSelStart = selStart - aboveLen;
  const nextSelEnd = selEnd - aboveLen;
  return { nextValue, nextSelStart, nextSelEnd };
}

// Move the [firstLine..lastLine] block down by one (swap with the line
// below). Returns updated state or null if at bottom.
export function moveLinesDown(value, selStart, selEnd) {
  const { lines, firstLine, lastLine } = lineRangeForSelection(value, selStart, selEnd);
  if (lastLine >= lines.length - 1) return null; // already at bottom
  const below = lines[lastLine + 1];
  const block = lines.slice(firstLine, lastLine + 1);
  const newLines = [
    ...lines.slice(0, firstLine),
    below,
    ...block,
    ...lines.slice(lastLine + 2),
  ];
  const nextValue = newLines.join('\n');
  const belowLen = below.length + 1;
  const nextSelStart = selStart + belowLen;
  const nextSelEnd = selEnd + belowLen;
  return { nextValue, nextSelStart, nextSelEnd };
}
